/**
 * stampDuty.ts — transfer (stamp) duty on the purchase of a home, by state and territory.
 *
 * WHY (owner, 23 Sep 2026). A downsize books the new home at its price and nothing was taken off for
 * the duty on buying it, so every downsize understated the changeover — the owner had been carrying
 * the duty in his head. Rather than a flat percentage, each jurisdiction's own scale is here, so the
 * app can put an ESTIMATE in front of the client that they can overwrite from a conveyancer's quote.
 *
 * WHAT IS MODELLED. The scale an owner-occupier pays on a residential purchase: where a jurisdiction
 * has a principal-place-of-residence or "home" scale (VIC, QLD, ACT), that is the one used, because
 * the new home is the partner's home. Where it has only a general residential scale (NSW, WA, TAS,
 * SA, NT), that. No pensioner, seniors, first-home or downsizer CONCESSIONS are applied (owner:
 * "concessions can be added later"), and no foreign-purchaser surcharge. The figure is therefore the
 * conservative one for most clients, and it is labelled an estimate wherever it appears.
 *
 * "$X per $100 or part thereof" is applied on the exact value rather than rounded up to the next
 * $100 — an understatement of under $6 on an estimate that will be overwritten by a quote.
 *
 * PROVENANCE. Each schedule records the page it was read from and the date that page stated, in the
 * manner of ratesAndThresholds.ts. Two revenue offices (SA, NT) refuse automated access; their
 * long-standing scales are recorded as UNVERIFIED with the page to confirm against, and the ACT page
 * showed its 2025-26 scale (the territory indexes on 1 July) — all three are listed for the
 * actuarial pack. NSW indexes its thresholds annually on 1 July; VIC, QLD, WA and TAS scales are
 * fixed by legislation and change rarely.
 */

export type StampDutyState = 'NSW' | 'VIC' | 'QLD' | 'SA' | 'WA' | 'TAS' | 'ACT' | 'NT';

/** Ordered for pickers. Same codes the retirement village rules use. */
export const STAMP_DUTY_STATES: Array<{ code: StampDutyState; name: string }> = [
    { code: 'NSW', name: 'New South Wales' },
    { code: 'VIC', name: 'Victoria' },
    { code: 'QLD', name: 'Queensland' },
    { code: 'SA', name: 'South Australia' },
    { code: 'WA', name: 'Western Australia' },
    { code: 'TAS', name: 'Tasmania' },
    { code: 'ACT', name: 'Australian Capital Territory' },
    { code: 'NT', name: 'Northern Territory' },
];

/**
 * One band of a marginal scale: for a value above `from` (and not above the next band's `from`),
 * duty is `base` plus `rate` × (value − `from`). A band with `flatOnWhole` charges `rate` on the
 * WHOLE value instead (VIC above $960,000; ACT above $1,455,000).
 */
export interface StampDutyBand {
    from: number;
    base: number;
    rate: number;
    flatOnWhole?: boolean;
}

export interface StampDutySchedule {
    state: StampDutyState;
    /** Which scale this is, in the revenue office's own words. */
    scale: string;
    bands: StampDutyBand[];
    /** A floor, where the scale has one (NSW $20, TAS $50). */
    minimum?: number;
    /**
     * NT charges a quadratic formula below its threshold rather than bands. Present only for NT:
     * duty = a·V² + b·V for V = value ÷ 1000, up to `threshold`; the bands then apply above it.
     */
    formulaBelow?: { threshold: number; a: number; b: number };
    /**
     * VIC's home scale stops at $550,000 and the general scale applies above it, discontinuously.
     * Present only for VIC: the general scale to use once the value passes `above`.
     */
    generalAbove?: { above: number; bands: StampDutyBand[] };
    source: { url: string; stated: string; verified: boolean; note?: string };
}

export const STAMP_DUTY_SCHEDULES: Record<StampDutyState, StampDutySchedule> = {
    NSW: {
        state: 'NSW',
        scale: 'Transfer duty, standard rates 2026-27 (thresholds indexed annually on 1 July)',
        minimum: 20,
        bands: [
            { from: 0, base: 0, rate: 0.0125 },
            { from: 18_000, base: 225, rate: 0.015 },
            { from: 38_000, base: 525, rate: 0.0175 },
            { from: 103_000, base: 1_662, rate: 0.035 },
            { from: 387_000, base: 11_602, rate: 0.045 },
            { from: 1_290_000, base: 52_237, rate: 0.055 },
            // Premium duty on residential land above the premium threshold.
            { from: 3_870_000, base: 194_137, rate: 0.07 },
        ],
        source: { url: 'https://www.revenue.nsw.gov.au/taxes-duties-levies-royalties/transfer-duty/understanding-transfer-duty/calculate-transfer-duty', stated: 'page last updated 19 August 2026; 2026/27 thresholds', verified: true },
    },
    VIC: {
        state: 'VIC',
        scale: 'Land transfer duty, principal place of residence rates to $550,000, general rates above',
        bands: [
            { from: 0, base: 0, rate: 0.014 },
            { from: 25_000, base: 350, rate: 0.024 },
            { from: 130_000, base: 2_870, rate: 0.05 },
            { from: 440_000, base: 18_370, rate: 0.06 },
        ],
        generalAbove: {
            above: 550_000,
            bands: [
                { from: 0, base: 0, rate: 0.014 },
                { from: 25_000, base: 350, rate: 0.024 },
                { from: 130_000, base: 2_870, rate: 0.06 },
                { from: 960_000, base: 0, rate: 0.055, flatOnWhole: true },
                { from: 2_000_000, base: 110_000, rate: 0.065 },
            ],
        },
        source: { url: 'https://www.sro.vic.gov.au/about-us/rates-and-statistics/current-rates/land-transfer-duty-principal-place-residence-current-rates', stated: 'pages last updated 10 July 2026; PPR scale from 6 May 2008, general scale from 1 July 2021', verified: true },
    },
    QLD: {
        state: 'QLD',
        scale: 'Transfer duty, home concession rates (the buyer will live in the home)',
        bands: [
            { from: 0, base: 0, rate: 0.01 },
            { from: 350_000, base: 3_500, rate: 0.035 },
            { from: 540_000, base: 10_150, rate: 0.045 },
            { from: 1_000_000, base: 30_850, rate: 0.0575 },
        ],
        source: { url: 'https://qro.qld.gov.au/duties/transfer-duty/calculate/home-concession-rates/', stated: 'page last updated 31 July 2026', verified: true, note: 'The home concession is the standard owner-occupier scale, not a pensioner concession; QRO states there is no additional concession for seniors.' },
    },
    SA: {
        state: 'SA',
        scale: 'Stamp duty on conveyances, residential',
        bands: [
            { from: 0, base: 0, rate: 0.01 },
            { from: 12_000, base: 120, rate: 0.02 },
            { from: 30_000, base: 480, rate: 0.03 },
            { from: 50_000, base: 1_080, rate: 0.035 },
            { from: 100_000, base: 2_830, rate: 0.04 },
            { from: 200_000, base: 6_830, rate: 0.0425 },
            { from: 250_000, base: 8_955, rate: 0.0475 },
            { from: 300_000, base: 11_330, rate: 0.05 },
            { from: 500_000, base: 21_330, rate: 0.055 },
        ],
        source: { url: 'https://www.revenuesa.sa.gov.au/stampduty/stamp-duty-on-conveyances/rates-of-stamp-duty', stated: 'UNVERIFIED — RevenueSA refused automated access on 23 September 2026; long-standing scale recorded from reference, to be confirmed against the page', verified: false },
    },
    WA: {
        state: 'WA',
        scale: 'Transfer duty, general (residential) rate',
        bands: [
            { from: 0, base: 0, rate: 0.019 },
            { from: 120_000, base: 2_280, rate: 0.0285 },
            { from: 150_000, base: 3_135, rate: 0.038 },
            { from: 360_000, base: 11_115, rate: 0.0475 },
            { from: 725_000, base: 28_453, rate: 0.0515 },
        ],
        source: { url: 'https://www.wa.gov.au/organisation/department-of-treasury-and-finance/transfer-duty-assessment', stated: 'page last updated 30 July 2026', verified: true },
    },
    TAS: {
        state: 'TAS',
        scale: 'Property transfer duty, standard scale (from 21 October 2013)',
        minimum: 50,
        bands: [
            { from: 0, base: 50, rate: 0 },
            { from: 3_000, base: 50, rate: 0.0175 },
            { from: 25_000, base: 435, rate: 0.0225 },
            { from: 75_000, base: 1_560, rate: 0.035 },
            { from: 200_000, base: 5_935, rate: 0.04 },
            { from: 375_000, base: 12_935, rate: 0.0425 },
            { from: 725_000, base: 27_810, rate: 0.045 },
        ],
        source: { url: 'https://www.sro.tas.gov.au/property-transfer-duties/rates-of-duty', stated: 'page last updated 14 June 2022; scale effective 21 October 2013', verified: true },
    },
    ACT: {
        state: 'ACT',
        scale: 'Conveyance duty, owner-occupier, non-commercial property, 2025-26 (indexed 1 July)',
        bands: [
            { from: 0, base: 0, rate: 0.0028 },
            { from: 260_000, base: 728, rate: 0.022 },
            { from: 300_000, base: 1_608, rate: 0.034 },
            { from: 500_000, base: 8_408, rate: 0.0432 },
            { from: 750_000, base: 19_208, rate: 0.059 },
            { from: 1_000_000, base: 33_958, rate: 0.064 },
            { from: 1_455_000, base: 0, rate: 0.0454, flatOnWhole: true },
        ],
        source: { url: 'https://www.revenue.act.gov.au/rates-and-property-charges/conveyance-duty-stamp-duty/conveyance-duty-for-non-commercial-property', stated: 'scale for 1 July 2025 to 30 June 2026 as shown on 23 September 2026; the 2026-27 scale was not yet displayed — confirm', verified: false },
    },
    NT: {
        state: 'NT',
        scale: 'Stamp duty on conveyances: formula to $525,000, flat rates above',
        formulaBelow: { threshold: 525_000, a: 0.06571441, b: 15 },
        bands: [
            { from: 525_000, base: 0, rate: 0.0495, flatOnWhole: true },
            { from: 3_000_000, base: 0, rate: 0.0575, flatOnWhole: true },
            { from: 5_000_000, base: 0, rate: 0.0595, flatOnWhole: true },
        ],
        source: { url: 'https://nt.gov.au/property/buying-and-selling-a-home/stamp-duty-when-buying-a-home', stated: 'UNVERIFIED — nt.gov.au refused automated access on 23 September 2026; long-standing formula recorded from reference (continuous at $525,000: both sides give $25,987.50), to be confirmed against the page', verified: false },
    },
};

export const STAMP_DUTY_PROVENANCE = {
    readOn: '23 September 2026',
    unverified: (['SA', 'NT', 'ACT'] as StampDutyState[]),
};
